import { Box, Typography } from "@mui/material";
import { t } from "i18next";
import { type FC, useCallback } from "react";
import { GradientStar } from "../../styles/icons/gradient-star";
import { type DialogController, useDialog } from "../../utils/hooks/use-dialog";
import { Carousel } from "../discover/carousel/carousel";
import { SeverityPill } from "../generic/components/severity-pill";
import { useAccountLimit } from "../generic/hooks/use-account-limit";
import { useUpsellServices } from "../generic/hooks/use-upsell-services";
import { usePageContentMx } from "../layout/components/page-content";
import { AdditionalServicesModal } from "./additional-services-modal";
import type { AdditionalServicesType, PossibleUpsellServices } from "./type";

const allServiceItems = [
  {
    key: "sms",
    name: "SMS",
    title: "SMS - A Quicker Way to Connect with Employees",
    description: "Simplify Your Communication",
    image: "https://images.changeengine.com/sms1.webp",
    service: "sms" as const,
    serviceEnabled: "enable_twilio" as const,
  },
  {
    key: "listen",
    name: "Listen",
    title: "Listen More, Act Faster - Surveys That Drive Results",
    description: "Find out about ChangeEngine Listen",
    image: "https://images.changeengine.com/listen-modal-2-header.webp",
    service: "listen" as const,
    serviceEnabled: "enable_listen" as const,
  },
  {
    key: "swag",
    name: "Swag",
    title: "Celebrate With Impact & Make Memories Stick!",
    description: "Explore the Swag Magic",
    image: "https://images.changeengine.com/swag.webp",
    service: "swag" as const,
    serviceEnabled: "enable_swag" as const,
  },
  {
    key: "egifting",
    name: "E-Gifting",
    title: "The Fastest Way to Make Recognition Unforgettable",
    description: "Upgrade Your Recognition Game",
    image: "https://images.changeengine.com/egifting.webp",
    service: "egifting" as const,
    serviceEnabled: "enable_e_gifting" as const,
  },
  {
    key: "videography",
    name: "Videography",
    title: "Bring Your Culture to Life Through Video!",
    description: "Complete the Experience with Video",
    image: "https://images.changeengine.com/videography.webp",
    service: "videography" as const,
    serviceEnabled: "enable_videography" as const,
  },
  {
    key: "designservices",
    name: "Design Services",
    title: "Fuel Your Vision With Design Firepower",
    description: "Supercharge Your Visuals",
    image: "https://images.changeengine.com/designservices.webp",
    service: "designservices" as const,
    serviceEnabled: "enable_design_services" as const,
  },
];

export const AdditionalServicesCarousel: FC = () => {
  const dialog = useDialog<{ service: AdditionalServicesType }>();
  const { data: listenEntitlement, loading: limitLoading } = useAccountLimit("listen_entitlement");
  const { loading, data: upsoldServices } = useUpsellServices();
  const pageContentMx = usePageContentMx();

  const serviceEnabled = (service: PossibleUpsellServices): boolean => {
    if (limitLoading || loading || !upsoldServices) return false;
    return upsoldServices[service] ?? false;
  };

  if (loading || !upsoldServices) return null;

  const filteredItems = allServiceItems.filter((item) => {
    if (item.key === "listen") {
      return listenEntitlement === "Disabled" && !serviceEnabled(item.serviceEnabled);
    }
    return !serviceEnabled(item.serviceEnabled);
  });

  if (filteredItems.length === 0) return null;

  return (
    <Box sx={{ backgroundColor: "background.default" }}>
      <Box
        sx={{
          pb: 4,
          pt: 4,
          mx: pageContentMx,
        }}
      >
        <>
          <Box sx={{ pb: 2 }}>
            <Typography fontWeight={700} variant="h6" display="flex" gap={1} color="text.primary">
              <GradientStar />
              {t("Do something special today")}
            </Typography>
          </Box>
          <Carousel
            items={filteredItems}
            gap={2}
            Item={({ value: props }) => (
              <ServiceCard
                {...props}
                data-analytics-id={`homepage-services-carousel-${props.key}`}
                key={props.key}
                dialog={dialog}
              />
            )}
            sx={(theme) => ({
              "--carousel-navigation-button-width": theme.spacing(6),
            })}
            itemWidth="24%"
            itemMinWidth="258px"
          />
        </>
      </Box>
      <AdditionalServicesModal dialog={dialog} />
    </Box>
  );
};

const ServiceCard: FC<{
  name: string;
  title: string;
  description: string;
  service: AdditionalServicesType;
  image: string;
  "data-analytics-id"?: string;
  dialog: DialogController<{
    service: AdditionalServicesType;
  }>;
}> = ({ name, title, description, service, image, "data-analytics-id": analyticsId, dialog }) => {
  const handleClick = useCallback(() => {
    dialog.handleOpen({ service });
  }, [dialog, service]);

  return (
    <Box
      data-analytics-id={analyticsId}
      onClick={handleClick}
      sx={{
        cursor: "pointer",
        minWidth: "258px",
        borderRadius: 2,
        overflow: "hidden",
        boxShadow: 2,
        backgroundColor: "white",
      }}
    >
      <Box
        sx={{
          position: "relative",
          borderRadius: 2,
          overflow: "hidden",
          boxShadow: 2,
          backgroundColor: "white",
          display: "flex",
          flexDirection: "column",
          height: "160px",
        }}
      >
        <Box sx={{ position: "absolute", top: 12, left: 12, zIndex: 1 }}>
          <SeverityPill color="primary">{name}</SeverityPill>
        </Box>
        <Box
          component="img"
          src={image}
          alt={title}
          sx={{
            maxHeight: "100%",
            maxWidth: "100%",
            objectFit: "contain",
          }}
        />
      </Box>
      <Box
        sx={{
          background: "linear-gradient(to right bottom, #FFDF00, #FFB300)",
          px: 2,
          py: 2,
          height: "120px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography variant="body1" fontWeight="bold" color="black" lineHeight={1.4}>
          {title}
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: "auto",
          }}
        >
          <Typography variant="body2" color="black" fontSize="0.79rem">
            {description}
          </Typography>
          <Typography variant="body2" color="black" fontWeight={500}>
            ›
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
